import React, { useCallback, useMemo } from 'react'
import clsx from 'clsx'
import * as R from 'ramda'
import { CheckIcon } from '~/assets/icons'
import { toast } from 'react-toastify'
import { useApi } from '~/store/store'
import { mutualTheme } from '~/utils/theme'
import { HfDropInput } from '../HfDropInput'
import { makeStyles } from '@material-ui/styles'
import { ServiceResponse } from '@mutual-libs/shared-types'
import { UploadFilesToS3Param, useIsMobileScreen, useS3Upload } from '~/hooks'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { AddNewDocumentButton } from '../AddNewDocumentButton'
import { UploadDialog, UploadDialogProps } from '../UploadDialog'
import { Box, Button, CircularProgress } from '@material-ui/core'
import { SubmitHandler, useForm, useFieldArray } from 'react-hook-form'
import { getFileType, getFileTypes, translateDocumentType } from '~/utils/documents'
import {
  DocumentsGroup,
  DocumentsGroupType,
  Document, DocumentAnalysisStatus,
  AddDocumentsOnDocumentGroupInput
} from '@mutual-services/account-documents-types'
import { trackEvent } from '~/service/analytics'
import { getOnDocumentationPendencyClickAction } from '~/utils/analyticsHelpers'
import { AccountAnalysisStatus } from '@mutual-services/account-types'
import { TrackEventType } from '@mutual-services/marketing-types'

type FormValues = {
  [K in DocumentsGroupType | string]: { file: File }[] | null
}

export type UploadPendenciesModalProps = {
  updateDocuments: () => void,
  document: ServiceResponse<DocumentsGroup>,
  documents: ServiceResponse<DocumentsGroup[]>
} & Omit<UploadDialogProps, 'title' | 'description'>

const useStyles = makeStyles({
  modalContainer: {
    minWidth: 500,
    [mutualTheme.breakpoints.down('md')]: {
      minWidth: '90%'
    }
  },
  saveButton: {
    height: 56,
    fontWeight: 700,
    textTransform: 'none',
    marginTop: mutualTheme.spacing(2),
    marginRight: mutualTheme.spacing(1)
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center'
  }
})

const generateFormInitialValues = (documentGroup: ServiceResponse<DocumentsGroup>): FormValues => {
  const documents = documentGroup.documents

  const initialValues = documents.reduce((acc, document) => {
    return {
      ...acc,
      [document.type]: { file: null }
    }
  }, {})

  return initialValues as FormValues
}

export const UploadPendenciesModal: React.FC<UploadPendenciesModalProps> = (props) => {
  const { onClose, containerClassName, isVisible, document, updateDocuments, documents } = props
  const { _id: accountId, email } = useAuthAccount()
  const [uploadFilesToS3, uploadFilesToS3State] = useS3Upload()
  const [changeDocumentsGroupStatus, changeDocumentsGroupStatusState] = useApi('changeDocumentsGroupStatus')
  const [addDocumentOnDocumentGroup, addDocumentOnDocumentGroupState] = useApi('addDocumentOnDocumentGroup')
  const [updatePJAnalysisStatus, updatePJAnalysisStatusState] = useApi('updatePJAnalysisStatus')
  const [sendDocumentsLead, sendDocumentsLeadState] = useApi('sendDocumentsLead')
  const isAdditionalDocument = document.type === DocumentsGroupType.ADDITIONAL
  const isMobile = useIsMobileScreen()

  const isLastPendingDocument = useMemo(() => documents.filter(document => document.status === DocumentAnalysisStatus.PENDING).length === 1, [documents])

  const isLoading = useMemo(() => {
    return (
      uploadFilesToS3State.loading ||
      addDocumentOnDocumentGroupState.loading ||
      changeDocumentsGroupStatusState.loading ||
      updatePJAnalysisStatusState.loading ||
      sendDocumentsLeadState.loading
    )
  }, [addDocumentOnDocumentGroupState.loading, uploadFilesToS3State.loading, changeDocumentsGroupStatusState.loading, updatePJAnalysisStatusState.loading])

  const { type: documentType, allowedDocumentTypes, typeName } = document

  const classes = useStyles()

  const { handleSubmit, control, register } = useForm<FormValues>({
    defaultValues: generateFormInitialValues(document)
  })

  const { append, remove, fields } = useFieldArray({
    control,
    name: `${documentType}-pendencies`
  })

  const canAddDocuments = useMemo(() => fields.length < 5, [fields])
  const hasOnlyOneFile = useMemo(() => fields.length <= 1, [fields])
  const formId = useMemo(() => `pendencies-modal-form${documentType}`, [documentType])
  const fileTypes = useMemo(() => getFileTypes(allowedDocumentTypes), [allowedDocumentTypes])
  const description = useMemo(() => document.timeline[document.timeline.length - 1].denialReason || (isAdditionalDocument ? document.typeDescription : ''), [document])
  const label = useMemo(() => isAdditionalDocument ? (document.typeName || translateDocumentType(documentType)) : translateDocumentType(documentType), [documentType, document])

  const appendInput = useCallback(() => {
    if (fields.length >= 5) {
      return
    }
    append({ file: null } as any)
  }, [append, fields])

  const removeInput = useCallback((index: number) => {
    if (fields.length === 1) {
      return
    }

    remove(index)
  }, [remove, fields])

  const completedAt = useMemo(() => {
    const filteredDocData = document.documents.map((item, index) => {
      return { ...item, type: `${item.type}.${index}` }
    })

    return filteredDocData.reduce((acc, item) => {
      return {
        ...acc,
        [item.type]: item.sentAt
      }
    }, {} as any)
  }, [document, documentType])

  const getDocumentsParams = useCallback((documents: { file: File }[]): AddDocumentsOnDocumentGroupInput => {
    const formatedDocuments = documents.map(document => {
      return {
        type: documentType,
        originalName: document.file.name,
        mimeType: getFileType(document.file.type)
      }
    })

    return {
      documents: formatedDocuments,
      documentsGroupId: document._id
    }
  }, [documentType, document])

  const onSubmit: SubmitHandler<FormValues> = useCallback(async (formValues: FormValues) => {
    const currentInput = formValues[`${documentType}-pendencies`]
    trackEvent(getOnDocumentationPendencyClickAction(document.type))

    if (!currentInput) {
      toast.error('Erro ao salvar os documentos pendentes. Tente novamente em alguns instantes.', {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })
      return
    }

    const completedInputs = currentInput.filter(document => document !== null)

    if (R.isEmpty(completedInputs)) {
      toast.error('Adicione pelo menos um documento a ser enviado', {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })
      return
    }

    const paramsToSend = getDocumentsParams(completedInputs)
    const addDocumentOnDocumentGroupResult = await addDocumentOnDocumentGroup(paramsToSend)

    if (addDocumentOnDocumentGroupResult.error) {
      toast.error('Erro ao retornar URLs assinadas para realizar upload.', {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })
      return
    }

    const getS3ParamsToSend = (documents: ServiceResponse<Document[]>): UploadFilesToS3Param => {
      const paramsToSend: UploadFilesToS3Param = []

      documents.forEach((document, index) => {
        const currentFile = currentInput[index]

        if (!currentFile) {
          return
        }

        paramsToSend.push({
          file: currentFile.file,
          url: document.uploadUrl
        })
      })

      return paramsToSend
    }

    const s3Params = getS3ParamsToSend(addDocumentOnDocumentGroupResult.data)
    const s3UploadResult = await uploadFilesToS3(s3Params)

    if (s3UploadResult.error) {
      toast.error('Erro ao realizar upload para o S3', {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })
      return
    }

    const changeDocumentsGroupStatusResult = await changeDocumentsGroupStatus({
      accountId,
      denialReason: null,
      documentsGroupId: document._id,
      status: DocumentAnalysisStatus.PENDING_ANALYSIS
    })

    if (changeDocumentsGroupStatusResult.ok) {
      toast.success(`${isAdditionalDocument ? (typeName || translateDocumentType(documentType)) : translateDocumentType(documentType)} enviado com sucesso!`, {
        autoClose: 3000,
        closeButton: false,
        position: toast.POSITION.TOP_RIGHT
      })

      await sendDocumentsLead({ accountId, email, eventType: TrackEventType.pjPendencySent })

      if (isLastPendingDocument) {
        await Promise.all([
          updatePJAnalysisStatus({
            accountId,
            pjAnalysisStatus: {
              status: AccountAnalysisStatus.WAITING_FOR_ANALYSIS_POST_PENDENCIES
            }
          }),
          sendDocumentsLead({ accountId, email, eventType: TrackEventType.pjDocSent })
        ])
      }

      updateDocuments()
      return
    }

    toast.error('Erro ao salvar os documentos. Tente novamente em alguns instantes.', {
      autoClose: 3000,
      closeButton: false,
      position: toast.POSITION.TOP_RIGHT
    })
  }, [isLastPendingDocument, sendDocumentsLead, email, updatePJAnalysisStatus, trackEvent, getOnDocumentationPendencyClickAction, updateDocuments, toast, getDocumentsParams, addDocumentOnDocumentGroup, uploadFilesToS3, changeDocumentsGroupStatus, translateDocumentType, documentType, accountId, typeName])

  return (
    <UploadDialog
      title={label}
      maxWidth='lg'
      onClose={onClose}
      isVisible={isVisible}
      description={description || '-'}
      containerClassName={clsx(classes.modalContainer, containerClassName)}
    >
      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
        {fields.map((field: any, index) => {
          return (
            <HfDropInput
              key={field.id}
              control={control}
              fileTypes={fileTypes}
              defaultValue={field.file}
              label={isMobile ? label : ''}
              canRemove={fields.length > 1}
              hasOnlyOneFile={hasOnlyOneFile}
              allowedDocumentTypes={allowedDocumentTypes}
              completedAt={completedAt[`${documentType}-pendencies.${index}`]}
              fieldArrayProps={{
                remove: () => removeInput(index)
              }}
              {...register(`${documentType}-pendencies.${index}.file` as const)}
            />
          )
        })}
        {canAddDocuments && (
          <AddNewDocumentButton
            label={label || ''}
            onClick={appendInput}
          />
        )}
        <form id={formId} className={classes.buttonContainer} onSubmit={handleSubmit(onSubmit)}>
          <Button
            type='submit'
            size='large'
            color='primary'
            variant='contained'
            disabled={isLoading}
            className={classes.saveButton}
            startIcon={isLoading ? <CircularProgress color='inherit' size={20} /> : <CheckIcon htmlColor={mutualTheme.palette.common.white} />}
          >
            Concluir importação
          </Button>
        </form>
      </Box>
    </UploadDialog>
  )
}
